import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { constat, services, steps, reasons } from "@/content/site";
import { Reveal } from "@/components/site/Motion";
import { SectionHead, TextLink } from "@/components/site/Ui";

export function Constat() {
  return (
    <section className="wrap py-20 md:py-32" data-testid="section-constat">
      <SectionHead label="Le constat" lines={["Vos clients vous cherchent déjà.", "Vous trouvent-ils ?"]}>
        Une fiche Google incomplète ou un simple compte Instagram, et c'est souvent le voisin qui reçoit l'appel.
      </SectionHead>
      <div className="mt-16 grid md:grid-cols-3">
        {constat.map((c, i) => (
          <Reveal key={c.n} delay={i * 0.08} className="border-t border-ink/20 py-8 md:border-l md:border-t-0 md:px-8 md:first:border-l-0 md:first:pl-0">
            <p className="font-mono text-[13px] text-accent">{c.n}</p>
            <h3 className="display mt-4 text-[1.9rem] leading-tight">{c.title}</h3>
            <p className="mt-3 max-w-xs leading-relaxed text-mute">{c.text}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function ExpertiseRows({ items = services }) {
  return (
    <div className="border-t border-ink/20">
      {items.map((s, i) => (
        <Reveal key={s.n} delay={i * 0.04}>
          <article id={s.id} className="group grid gap-4 border-b border-ink/20 py-8 transition-colors duration-500 hover:bg-white/50 md:grid-cols-12 md:items-baseline md:px-4 md:py-10" data-testid={`expertise-${s.id}`}>
            <span className="font-mono text-[13px] text-mute md:col-span-1">{s.n}</span>
            <h3 className="display text-[2rem] leading-none transition-transform duration-500 group-hover:translate-x-1.5 md:col-span-5 md:text-[2.6rem]">{s.title}</h3>
            <div className="md:col-span-5 md:col-start-8">
              <p className="leading-relaxed text-mute">{s.text}</p>
              <TextLink to={s.to} className="mt-4" testid={`expertise-cta-${s.id}`}>{s.cta}</TextLink>
            </div>
          </article>
        </Reveal>
      ))}
    </div>
  );
}

export function Methode() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 80%", "end 60%"] });
  const scale = useTransform(scrollYProgress, [0, 1], [0, 1]);
  return (
    <section ref={ref} className="wrap relative py-20 md:py-32" data-testid="section-methode">
      <SectionHead label="Méthode" lines={["Cinq étapes, sans jargon."]} className="mb-14 md:mb-20" />
      <div className="relative">
        <div className="absolute left-[7px] top-0 h-full w-px bg-ink/20 md:left-0 md:top-[7px] md:h-px md:w-full" />
        <motion.div style={{ scaleX: scale }} className="absolute left-0 top-[7px] hidden h-px w-full origin-left bg-accent md:block" />
        <motion.div style={{ scaleY: scale }} className="absolute left-[7px] top-0 h-full w-px origin-top bg-accent md:hidden" />
        <ol className="grid gap-10 md:grid-cols-5 md:gap-8">
          {steps.map((s, i) => (
            <Reveal as="li" key={s.n} delay={i * 0.06} className="relative pl-10 md:pl-0 md:pt-12">
              <span className="absolute left-0 top-0 h-[15px] w-[15px] border border-ink bg-ivory" />
              <p className="font-mono text-[13px] text-mute">{s.n}</p>
              <h3 className="display mt-2 text-[1.8rem] leading-tight">{s.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-mute">{s.text}</p>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function Pourquoi() {
  return (
    <section className="wrap py-20 md:py-32" data-testid="section-pourquoi">
      <div className="grid gap-12 lg:grid-cols-12">
        <SectionHead className="lg:col-span-5" label="Pourquoi moi" lines={["Un beau site ne suffit pas.", "Il doit faire venir des clients."]} />
        <ul className="grid gap-px bg-ink/15 sm:grid-cols-2 lg:col-span-7">
          {reasons.map((r, i) => (
            <Reveal as="li" key={r} delay={i * 0.04} className="bg-ivory p-6">
              <p className="font-mono text-[13px] text-mute">0{i + 1}</p>
              <p className="mt-3 text-[17px] font-medium leading-snug">{r}</p>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}
